/**
 * api.ts
 * Thin fetch wrapper around the N73AA API Gateway.
 * Reads VITE_API_BASE_URL at build time.
 * Attaches Authorization: Bearer <idToken> on every request.
 */

import type {
  AppConfig,
  ApiResult,
  FlightPayload,
  FuelPayload,
  ExpensePayload,
  PresignedUrlResponse,
  EntryTab,
  QuarterSel,
  EntryScope,
  EntriesResponse,
  UpdateFlightFields,
  UpdateFuelFields,
  UpdateExpenseFields,
  DeleteEntryPayload,
  ExpenseAccountsResponse,
  ReadPhotoType,
  ReadPhotoResponse,
  ReadTachExtracted,
  ReadFuelExtracted,
  ReadExpenseExtracted,
} from './types'

// Same-origin: CloudFront proxies /api/* to API Gateway. VITE_API_BASE_URL
// stays empty for stage/prod; can be set to a full origin if you ever run the
// SPA against a deployed API from somewhere else (e.g. a local dev origin).
const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) ?? ''

// Token accessor — set by AuthProvider after sign-in.
let _idToken: string | null = null

export function setApiToken(token: string | null): void {
  _idToken = token
}

// Callback fired when ANY API call returns 401 (expired / revoked token).
// AuthProvider registers signOut() here so a stale session immediately drops
// the user back to the sign-in page without requiring a manual refresh.
let _onUnauthorized: (() => void) | null = null

export function setOnUnauthorized(handler: (() => void) | null): void {
  _onUnauthorized = handler
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

async function request<T>(
  method: 'GET' | 'POST',
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (_idToken) {
    headers['Authorization'] = `Bearer ${_idToken}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (res.status === 401) {
    // Token expired/revoked — clear local auth state so RequireAuth bounces
    // the user back to the sign-in page on the next render. Without this the
    // user is stuck on a broken page until they manually refresh.
    if (_onUnauthorized) _onUnauthorized()
    throw new ApiError(401, 'Session expired. Please sign in again.')
  }
  if (res.status === 403) {
    throw new ApiError(403, 'Your account is not authorized.')
  }
  if (!res.ok) {
    let msg = `Server error (${res.status})`
    try {
      const json = (await res.json()) as { message?: string }
      if (json.message) msg = json.message
    } catch {
      // ignore parse error
    }
    throw new ApiError(res.status, msg)
  }

  return res.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Fetch partners list, current tach, and default airport. */
export async function getConfig(): Promise<AppConfig> {
  return request<AppConfig>('GET', '/api/config')
}

/** Submit a flight log entry. */
export async function submitFlight(payload: FlightPayload): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/submitFlight', payload)
}

/** Submit a fuel purchase entry. */
export async function submitFuel(payload: FuelPayload): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/submitFuel', payload)
}

/** Submit an expense entry. */
export async function submitExpense(payload: ExpensePayload): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/submitExpense', payload)
}

/**
 * Request a presigned S3 URL for a receipt photo upload.
 * filename: suggested file name (e.g. "fuel_2025-06-01_ES.jpg")
 * contentType: MIME type ("image/jpeg")
 */
export async function getReceiptUploadUrl(
  filename: string,
  contentType: string,
): Promise<PresignedUrlResponse> {
  return request<PresignedUrlResponse>('POST', '/api/receiptUploadUrl', {
    filename,
    contentType,
  })
}

/**
 * PUT a Blob/File directly to the presigned S3 URL.
 * This call does NOT go through the API Gateway — it goes straight to S3.
 * No Authorization header is included (presigned URL carries its own creds).
 */
export async function uploadReceipt(
  presignedUrl: string,
  blob: Blob,
): Promise<void> {
  const res = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': blob.type || 'image/jpeg' },
    body: blob,
  })
  if (!res.ok) {
    throw new Error(`Receipt upload failed (${res.status})`)
  }
}

/**
 * Fetch log entries for a given tab, quarter, and scope.
 * scope: 'mine' (default — only caller's rows) | 'all' (every partner's rows)
 */
export async function getEntries(
  tab: EntryTab,
  quarter: QuarterSel,
  scope: EntryScope = 'mine',
): Promise<EntriesResponse> {
  return request<EntriesResponse>('GET', `/api/entries?tab=${tab}&quarter=${quarter}&scope=${scope}`)
}

/**
 * Edit one of the caller's own current-quarter rows. Server enforces both
 * ownership and quarter gates — see lambda/src/updateEntry.ts. The handler
 * dispatches on `tab`; we expose three typed wrappers for clarity.
 */
export async function updateFlight(
  rowIndex: number,
  fields: UpdateFlightFields,
): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/updateEntry', {
    tab: 'flights',
    rowIndex,
    fields,
  })
}

export async function updateFuel(
  rowIndex: number,
  fields: UpdateFuelFields,
): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/updateEntry', {
    tab: 'fuel',
    rowIndex,
    fields,
  })
}

export async function updateExpense(
  rowIndex: number,
  fields: UpdateExpenseFields,
): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/updateEntry', {
    tab: 'expenses',
    rowIndex,
    fields,
  })
}

/** Hard-delete one of the caller's own current-quarter rows. */
export async function deleteEntry(payload: DeleteEntryPayload): Promise<ApiResult> {
  return request<ApiResult>('POST', '/api/deleteEntry', payload)
}

export { ApiError }

// ---------------------------------------------------------------------------
// Wave expense-account dropdown (appended)
// ---------------------------------------------------------------------------

/**
 * Fetch the curated list of Wave expense accounts available for the
 * Category dropdown on the expense form. Cached server-side per Lambda
 * container — the client just fires this once on Expense page mount.
 */
export async function getExpenseAccounts(): Promise<ExpenseAccountsResponse> {
  return request<ExpenseAccountsResponse>('GET', '/api/expenseAccounts')
}

// ---------------------------------------------------------------------------
// readPhoto — Bedrock vision OCR for tach displays and receipts
// ---------------------------------------------------------------------------

/**
 * Ask the backend to read structured data out of a previously-uploaded photo.
 *
 * Pass the `key` returned by `getReceiptUploadUrl()` after the PUT completes,
 * plus one of:
 *   - 'tach'    → reads the digital "TACH: <hours>" value from the bottom-left
 *                 status bar of the central avionics display
 *   - 'fuel'    → extracts gallons / total / vendor / date / airport from a
 *                 fuel-pump or FBO receipt
 *   - 'expense' → extracts amount / vendor / date / suggested Wave category
 *                 from a general business receipt
 *
 * Returns the model's structured JSON. Always check `extracted.confidence` —
 * 'low' means the model couldn't read the photo; surface that to the partner
 * and fall back to manual entry rather than auto-filling with junk.
 */
async function readPhoto(
  key: string,
  type: ReadPhotoType,
): Promise<ReadPhotoResponse> {
  return request<ReadPhotoResponse>('POST', '/api/readPhoto', { key, type })
}

/** Typed wrapper: read tach hours from an avionics-panel photo. */
export async function readTachPhoto(key: string): Promise<ReadTachExtracted> {
  const res = await readPhoto(key, 'tach')
  return res.extracted as ReadTachExtracted
}

/** Typed wrapper: read fields from a fuel receipt photo. */
export async function readFuelReceipt(key: string): Promise<ReadFuelExtracted> {
  const res = await readPhoto(key, 'fuel')
  return res.extracted as ReadFuelExtracted
}

/** Typed wrapper: read fields from a general expense receipt photo. */
export async function readExpenseReceipt(key: string): Promise<ReadExpenseExtracted> {
  const res = await readPhoto(key, 'expense')
  return res.extracted as ReadExpenseExtracted
}
